import React from 'react';
import { Sun, Moon, LogOut, Settings, User, Bell, ChevronDown, Eye, EyeOff, Calculator, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import UserAvatar from '../common/UserAvatar';
import logoConnect10 from '../../assets/logo-connect10.png';

/**
 * @file Componente de cabeçalho da aplicação.
 * Exibe o logo, saudação ao usuário e o menu de usuário com várias ações.
 *
 * @param {object} props - As propriedades do componente.
 */
const Header = ({
    user,
    userData,
    notifications = [],
    theme,
    onToggleTheme,
    onLogout,
    onOpenProfile,
    onOpenAdminPanel,
    onOpenNotifications,
    onOpenEffortCalculator,
    onOpenAbout,
    showDashboardData,
    onToggleDashboardVisibility
}) => {
    const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);

    const handleMenuAction = (action) => {
        setIsUserMenuOpen(false);
        action();
    };

    return (
        <header className="mb-8 flex justify-between items-center px-4">
            <img src={logoConnect10} alt="Logo Connect10" className="h-10 sm:h-12" />
            <div className="flex items-center gap-x-4">
                {user?.displayName && (
                    <div className="flex items-center gap-x-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                        {/* A saudação é ocultada em telas pequenas (mobile) */}
                        <span className="hidden sm:inline">Olá, {user.displayName.split(' ')[0]}!</span>
                        
                        {/* --- CORREÇÃO APLICADA AQUI --- */}
                        {/* O botão de visibilidade agora só é renderizado se o usuário for um administrador */}
                        {userData?.role === 'administrador' && (
                            <button
                                onClick={onToggleDashboardVisibility}
                                title={showDashboardData ? "Ocultar dados do gráfico" : "Exibir dados do gráfico"}
                                aria-label={showDashboardData ? "Ocultar dados do gráfico" : "Exibir dados do gráfico"}
                                aria-pressed={showDashboardData}
                                className="p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-900"
                            >
                                {showDashboardData ? (
                                    <EyeOff className="h-5 w-5" />
                                ) : (
                                    <Eye className="h-5 w-5" />
                                )}
                            </button>
                        )}
                    </div>
                )}
                <div className="relative">
                    <button
                        id="user-menu-button"
                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                        className="flex items-center gap-2 p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-900"
                        aria-label="Menu do usuário"
                        aria-expanded={isUserMenuOpen}
                        aria-haspopup="true"
                    >
                        <UserAvatar user={user} size="h-10 w-10" />
                        <ChevronDown className={`h-5 w-5 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {notifications.length > 0 && (
                        <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center ring-2 ring-white dark:ring-slate-800 pointer-events-none">
                            {notifications.length}
                        </span>
                    )}
                    <AnimatePresence>
                        {isUserMenuOpen && (
                            <motion.div
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="user-menu-button"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                                className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-md shadow-lg py-1 z-20"
                            >
                                <button role="menuitem" onClick={() => handleMenuAction(onOpenProfile)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:outline-none flex items-center"><User className="mr-2 h-4 w-4" />Meu Perfil</button>
                                <button role="menuitem" onClick={() => handleMenuAction(onOpenNotifications)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:outline-none flex items-center justify-between">
                                    <span className="flex items-center"><Bell className="mr-2 h-4 w-4" />Notificações</span>
                                    {notifications.length > 0 && <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">{notifications.length}</span>}
                                </button>
                                <button role="menuitem" onClick={() => handleMenuAction(onToggleTheme)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:outline-none flex items-center justify-between">
                                    {theme === 'light' ? (
                                        <span className="flex items-center"><Moon className="mr-2 h-4 w-4" /> Modo Escuro</span>
                                    ) : (
                                        <span className="flex items-center"><Sun className="mr-2 h-4 w-4" /> Modo Claro</span>
                                    )}
                                </button>
                                <button role="menuitem" onClick={() => handleMenuAction(onOpenEffortCalculator)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:outline-none flex items-center"><Calculator className="mr-2 h-4 w-4" />Cálculo de Esforço</button>
                                {userData?.role === 'administrador' && (
                                    <button role="menuitem" onClick={() => handleMenuAction(onOpenAdminPanel)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:outline-none flex items-center"><Settings className="mr-2 h-4 w-4" />Administração</button>
                                )}
                                <button role="menuitem" onClick={() => handleMenuAction(onOpenAbout)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:outline-none flex items-center hidden sm:flex"><Info className="mr-2 h-4 w-4" />Sobre</button>
                                <div className="border-t border-slate-200 dark:border-slate-700 my-1"></div>
                                <button role="menuitem" onClick={() => handleMenuAction(onLogout)} className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:outline-none flex items-center"><LogOut className="mr-2 h-4 w-4" />Sair</button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </header>
    );
};

export default Header;
